/**
 * Reporting periods.
 *
 * The app shows either a financial year (Apr–Mar) or a calendar year, switchable, with
 * the choice remembered as a preference (FR-DASH-08, FR-SET-01). The global period
 * selector governs the dashboard, transactions and reports at once (WIREFRAMES §1.1).
 *
 * Pure: no React, no Firebase.
 */

import {
  DEFAULT_FY_START_MONTH,
  addDays,
  assertDateKey,
  calendarYearRange,
  daysBetween,
  formatDateKey,
  formatMonthKey,
  fyKeyOf,
  fyRange,
  isValidDateKey,
  monthKeyOf,
  monthRange,
  monthsBetween,
  type DateKey,
  type DateRange,
  type MonthKey,
} from './dates';

export type PeriodBasis = 'fy' | 'calendar';
export type PeriodScope = 'year' | 'month' | 'custom';

export interface Period {
  basis: PeriodBasis;
  scope: PeriodScope;
  /**
   * `2026-27` for an FY, `2026` for a calendar year, `2026-08` for a month, and
   * `2026-01-01..2026-07-31` for a custom range.
   *
   * One string rather than a union of shapes, so a period stays a value that can be
   * compared, stored in a preference, and used as a React dependency without a deep
   * comparison.
   */
  key: string;
}

const CUSTOM_SEPARATOR = '..';

/**
 * An arbitrary from–to range (FR-RPT-01), added 4 Aug 2026.
 *
 * Month and year periods cannot express some real requests. The migrated archive is
 * 1 Jan – 31 Jul 2026, which crosses the financial-year boundary on 31 March and so is
 * neither one FY nor one calendar year; a tax quarter and a part-month export are the
 * same problem. This is not a departure from SD-08 — a `dateKey` range *is* a bounded
 * period, and arguably a more direct one than a `monthKey` equality, leaving the single
 * further server-side filter untouched.
 */
export function customPeriod(start: DateKey, end: DateKey, basis: PeriodBasis = 'fy'): Period {
  assertDateKey(start, 'range start');
  assertDateKey(end, 'range end');
  // Swapped rather than rejected: a date picker makes this easy to do by accident, and
  // there is exactly one thing the user can have meant.
  const [from, to] = start <= end ? [start, end] : [end, start];
  return { basis, scope: 'custom', key: `${from}${CUSTOM_SEPARATOR}${to}` };
}

export function isCustom(period: Period): boolean {
  return period.scope === 'custom';
}

/** Returns null for any period that is not a custom range. */
export function customRange(period: Period): DateRange | null {
  if (period.scope !== 'custom') return null;
  const [start, end] = period.key.split(CUSTOM_SEPARATOR);
  if (!isValidDateKey(start) || !isValidDateKey(end)) return null;
  return { start, end };
}

export interface PeriodOptions {
  fyStartMonth?: number;
}

export function currentPeriod(
  today: DateKey,
  basis: PeriodBasis,
  scope: PeriodScope = 'year',
  { fyStartMonth = DEFAULT_FY_START_MONTH }: PeriodOptions = {},
): Period {
  // There is no "current" custom range; the nearest sensible thing is the month, which
  // is what the picker opens on before the user narrows it.
  if (scope === 'custom') return { basis, scope: 'month', key: monthKeyOf(today) };
  if (scope === 'month') return { basis, scope, key: monthKeyOf(today) };
  return {
    basis,
    scope,
    key: basis === 'fy' ? fyKeyOf(today, fyStartMonth) : today.slice(0, 4),
  };
}

export function periodRange(
  period: Period,
  { fyStartMonth = DEFAULT_FY_START_MONTH }: PeriodOptions = {},
): DateRange {
  const custom = customRange(period);
  if (custom) return custom;
  if (period.scope === 'month') return monthRange(period.key);
  if (period.basis === 'fy') return fyRange(period.key, fyStartMonth);
  return calendarYearRange(Number(period.key));
}

/** The month keys a period spans — what the dashboard reads rollups for. */
export function periodMonths(period: Period, options: PeriodOptions = {}): MonthKey[] {
  if (period.scope === 'month') return [period.key];
  const { start, end } = periodRange(period, options);
  return monthsBetween(start.slice(0, 7), end.slice(0, 7));
}

export function periodLabel(period: Period): string {
  const custom = customRange(period);
  if (custom) {
    return `${formatDateKey(custom.start, 'dd MMM yyyy')} – ${formatDateKey(custom.end, 'dd MMM yyyy')}`;
  }
  if (period.scope === 'month') return formatMonthKey(period.key);
  return period.basis === 'fy' ? `FY ${period.key}` : period.key;
}

/**
 * Step a period forward or back. Used by the selector's arrows.
 *
 * Takes no `fyStartMonth`: shifting a year key increments the year regardless of which
 * month the year starts in. The start month only matters when *deriving* a period from a
 * date, which is `currentPeriod`'s job.
 */
export function shiftPeriod(period: Period, delta: number): Period {
  // A custom range shifts by its own length, so the arrows step through consecutive
  // windows of the same size — the only reading of "next" that makes sense for a range
  // the user chose. An unparseable key is returned unchanged rather than guessed at.
  const custom = customRange(period);
  if (custom) {
    const span = daysBetween(custom.start, custom.end) + 1;
    return customPeriod(
      addDays(custom.start, span * delta),
      addDays(custom.end, span * delta),
      period.basis,
    );
  }
  if (period.scope === 'custom') return period;

  if (period.scope === 'month') {
    const [y, m] = period.key.split('-').map(Number);
    const zeroBased = y * 12 + (m - 1) + delta;
    const key = `${String(Math.floor(zeroBased / 12)).padStart(4, '0')}-${String(
      (zeroBased % 12) + 1,
    ).padStart(2, '0')}`;
    return { ...period, key };
  }

  if (period.basis === 'calendar') {
    return { ...period, key: String(Number(period.key) + delta) };
  }

  const startYear = Number(period.key.slice(0, 4)) + delta;
  return { ...period, key: `${startYear}-${String((startYear + 1) % 100).padStart(2, '0')}` };
}

/**
 * Switch basis while staying on whatever period contains `anchor`.
 *
 * Without an anchor, toggling FY→calendar mid-year would jump the user somewhere
 * unrelated: FY 2026-27 has no single obvious calendar-year counterpart.
 */
export function switchBasis(
  period: Period,
  basis: PeriodBasis,
  anchor: DateKey,
  options: PeriodOptions = {},
): Period {
  if (period.basis === basis) return period;
  // A custom range means the same dates whichever basis is selected, so switching keeps
  // the range and only records the preference. Falling through to `currentPeriod` would
  // silently discard the range the user typed.
  if (period.scope === 'custom') return { ...period, basis };
  return currentPeriod(anchor, basis, period.scope, options);
}

/** Recent periods for the selector dropdown, newest first. */
export function recentPeriods(
  today: DateKey,
  basis: PeriodBasis,
  count = 5,
  options: PeriodOptions = {},
): Period[] {
  const now = currentPeriod(today, basis, 'year', options);
  return Array.from({ length: count }, (_, i) => shiftPeriod(now, -i));
}
